import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  LayoutDashboard,
  FileText,
  DollarSign,
  Wrench,
  Users,
  Monitor,
  CreditCard,
  MessageSquare,
  FileCheck,
  ShieldCheck,
  Calendar,
  Camera,
  Receipt,
  BarChart3,
  TrendingUp,
  Calculator,
  ClipboardList,
} from 'lucide-react';
import IconLogo from '../assets/Icon_logo.svg';
import LandingHeader from '../components/LandingHeader';
import { useLanguage } from '../contexts/LanguageContext';

const FEATURE_CONFIG = [
  {
    key: 'propertyManagement',
    categoryKey: 'category',
    icon: Building2,
    accent: 'slate',
    items: [
      { key: 'dashboard', icon: LayoutDashboard },
      { key: 'documents', icon: FileText },
      { key: 'financial', icon: DollarSign },
      { key: 'maintenance', icon: Wrench },
    ],
  },
  {
    key: 'tenantManagement',
    categoryKey: 'category',
    icon: Users,
    accent: 'slate',
    items: [
      { key: 'portal', icon: Monitor },
      { key: 'rent', icon: CreditCard },
      { key: 'communication', icon: MessageSquare },
      { key: 'lease', icon: FileCheck },
    ],
  },
  {
    key: 'serviceProvider',
    categoryKey: 'category',
    icon: Wrench,
    accent: 'slate',
    items: [
      { key: 'verified', icon: ShieldCheck },
      { key: 'booking', icon: Calendar },
      { key: 'tracking', icon: Camera },
      { key: 'cost', icon: Receipt },
    ],
  },
  {
    key: 'analytics',
    categoryKey: 'category',
    icon: BarChart3,
    accent: 'slate',
    items: [
      { key: 'performance', icon: BarChart3 },
      { key: 'market', icon: TrendingUp },
      { key: 'roi', icon: Calculator },
      { key: 'reports', icon: ClipboardList },
    ],
  },
];

const STATS = [
  { value: '500+', label: 'Properties Managed' },
  { value: '24/7', label: 'Support' },
  { value: '4.8', label: 'User Rating' },
  { value: '99%', label: 'Uptime' },
];

const FeaturesPage = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-white">
      <LandingHeader />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200/60 bg-gradient-to-b from-slate-50 to-white py-24 sm:py-32">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(148,163,184,0.15),transparent)]" />
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <span className="mb-4 inline-block rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-medium uppercase tracking-wider text-slate-600 shadow-sm">
            Features
          </span>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            {t('featuresPage.hero.title')}
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-600 sm:text-xl">
            {t('featuresPage.hero.subtitle')}
          </p>
          <Link
            to="/register"
            className="mt-10 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-slate-900/20 transition hover:bg-slate-800 hover:shadow-xl"
          >
            {t('featuresPage.hero.button')}
            <ArrowRight className="h-5 w-5" strokeWidth={2} />
          </Link>
        </div>
      </section>

      {/* Features – alternating blocks */}
      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-6xl px-6">
          {FEATURE_CONFIG.map((config, index) => {
            const CategoryIcon = config.icon;
            const isEven = index % 2 === 0;
            const categoryLabel = t(`featuresPage.features.${config.key}.${config.categoryKey}`);

            return (
              <div
                key={config.key}
                className={`flex min-h-0 flex-col gap-12 lg:flex-row lg:items-center lg:gap-16 ${index > 0 ? 'mt-28 lg:mt-36' : ''}`}
              >
                {/* Content side */}
                <div className={`flex-1 ${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
                  <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50">
                      <CategoryIcon className="h-7 w-7 text-slate-700" strokeWidth={1.5} />
                    </div>
                    <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                      {categoryLabel}
                    </h2>
                  </div>
                  <ul className="mt-8 space-y-6">
                    {config.items.map((item) => {
                      const ItemIcon = item.icon;
                      const title = t(`featuresPage.features.${config.key}.${item.key}.title`);
                      const description = t(`featuresPage.features.${config.key}.${item.key}.description`);
                      return (
                        <li key={item.key} className="group flex gap-4">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600 transition group-hover:bg-slate-200 group-hover:text-slate-800">
                            <ItemIcon className="h-5 w-5" strokeWidth={1.5} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{title}</h3>
                            <p className="mt-1 text-slate-600 leading-relaxed">{description}</p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                {/* Visual side – large icon card */}
                <div className={`flex-shrink-0 lg:w-[42%] ${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
                  <div className="rounded-3xl border border-slate-200/80 bg-gradient-to-br from-slate-50 to-slate-100/50 p-12 shadow-inner">
                    <div className="flex aspect-square max-w-sm items-center justify-center rounded-2xl bg-white shadow-sm border border-slate-200/60 mx-auto">
                      <CategoryIcon className="h-32 w-32 text-slate-300" strokeWidth={1} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-slate-200 bg-slate-50/80 py-12">
        <div className="mx-auto max-w-6xl px-6">
          <p className="mb-10 text-center text-sm font-medium uppercase tracking-wider text-slate-500">
            Trusted by property owners
          </p>
          <div className="grid grid-cols-2 gap-8 sm:grid-cols-4">
            {STATS.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                  {stat.value}
                </div>
                <div className="mt-1 text-sm text-slate-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-amber-200 bg-amber-400 py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-amber-950 sm:text-4xl">
            {t('featuresPage.cta.title')}
          </h2>
          <p className="mt-5 text-lg text-amber-900/90">
            {t('featuresPage.cta.subtitle')}
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/register"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-amber-950 px-8 py-4 text-base font-semibold text-amber-50 shadow-lg transition hover:bg-amber-900 sm:w-auto"
            >
              {t('featuresPage.cta.earlyAccess')}
              <ArrowRight className="h-5 w-5" strokeWidth={2} />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex w-full items-center justify-center rounded-xl border-2 border-amber-800 px-8 py-4 text-base font-semibold text-amber-950 transition hover:bg-amber-500/30 sm:w-auto"
            >
              {t('featuresPage.cta.viewPricing')}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-800 bg-slate-950 py-14 text-white">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <img className="h-10 w-auto opacity-90" src={IconLogo} alt="EasyProperty" />
              <p className="mt-4 text-sm text-slate-400">{t('footer.description')}</p>
            </div>
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-300">
                {t('footer.platform')}
              </h3>
              <ul className="mt-4 space-y-3 text-sm text-slate-400">
                <li>
                  <Link to="/register" className="transition hover:text-white">{t('footer.signUp')}</Link>
                </li>
                <li>
                  <Link to="/login" className="transition hover:text-white">{t('footer.signIn')}</Link>
                </li>
                <li>
                  <Link to="/features" className="transition hover:text-white">{t('footer.features')}</Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-300">
                {t('footer.support')}
              </h3>
              <ul className="mt-4 space-y-3 text-sm text-slate-400">
                <li><button className="text-left transition hover:text-white">{t('footer.helpCenter')}</button></li>
                <li><button className="text-left transition hover:text-white">{t('footer.contactUs')}</button></li>
                <li><button className="text-left transition hover:text-white">{t('footer.privacyPolicy')}</button></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-300">
                {t('footer.connect')}
              </h3>
              <ul className="mt-4 space-y-3 text-sm text-slate-400">
                <li><button className="text-left transition hover:text-white">{t('footer.aboutUs')}</button></li>
                <li><button className="text-left transition hover:text-white">{t('footer.blog')}</button></li>
                <li><button className="text-left transition hover:text-white">{t('footer.careers')}</button></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 border-t border-slate-800 pt-8 text-center text-sm text-slate-500">
            <p>&copy; 2024 EasyProperty. {t('footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FeaturesPage;
